import React from 'react';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const Row = ({ label, value, bold }) => (
  <div className="cwb-public-summary-row">
    <span className={`cwb-public-summary-row-label ${bold ? 'is-bold' : ''}`}>{label}</span>
    <span className={`cwb-public-summary-row-value ${bold ? 'is-bold' : ''}`}>{value}</span>
  </div>
);

const Divider = () => <div className="cwb-public-summary-divider" />;

const CustomTotalsTable = ({
  ticketTypes,
  promoCode,
  ticketReservation,
  addOns,
  confirm,
  ticketInsurance,
  selectedMembershipProduct,
}) => {
  const membershipPrice = selectedMembershipProduct && selectedMembershipProduct.ticketing_price
    ? selectedMembershipProduct.ticketing_price
    : null;
  const membershipSubtotal = membershipPrice ? (membershipPrice.amount_cents || 0) / 100 : 0;
  const membershipFees = membershipPrice && !selectedMembershipProduct.absorb_fees
    ? (membershipPrice.total_fee_amount_cents || 0) / 100
    : 0;
  const membershipTax = membershipPrice
    ? (membershipPrice.total_tax_amount_cents || 0) / 100
    : 0;
  const membershipTotal = membershipPrice ? (membershipPrice.customer_pays_cents || 0) / 100 : 0;
  const ticketFees = ticketReservation.in_memory_fees || [];

  const hasContent = Object.keys(ticketReservation).length > 0 || selectedMembershipProduct;
  if (!hasContent) return null;

  const purchasedTicketTypes = ticketTypes.filter((tt) => tt.quantity && tt.quantity > 0);
  const purchasedAddOns = addOns.filter((ao) => ao.quantity && ao.quantity > 0);

  const showPromoLine =
    Object.keys(promoCode).length > 0 &&
    typeof ticketReservation.discount !== 'undefined' &&
    ticketReservation.discount > 0;

  const subtotalDisplay = formatter.format(
    parseFloat(ticketReservation.subtotal_with_discount || 0) + membershipSubtotal
  );
  const totalDisplay = formatter.format(
    parseFloat(ticketReservation.gift_card_adjusted_total || 0) + membershipTotal
  );

  return (
    <div className="cwb-public-order-summary">
      <div className="cwb-public-summary-title">Order Summary</div>

      {/* Line items */}
      {purchasedTicketTypes.map((ticketType, index) => (
        ticketType.seatsIOObjects && ticketType.seatsIOObjects.length > 0 ? (
          ticketType.seatsIOObjects.map((object, oi) => (
            <Row
              key={`${index}-${oi}`}
              label={`${ticketType.name}: ${object.seat_assignment}`}
              value={ticketType.free ? 'FREE' : ticketType.price_to_currency}
            />
          ))
        ) : (
          <Row
            key={index}
            label={`${ticketType.quantity} x ${ticketType.name}`}
            value={ticketType.free ? 'FREE' : ticketType.price_to_currency}
          />
        )
      ))}

      {purchasedAddOns.map((addOn, index) => (
        addOn.seatsIOObjects && addOn.seatsIOObjects.length > 0 ? (
          addOn.seatsIOObjects.map((object, oi) => (
            <Row
              key={`${index}-${oi}`}
              label={`${addOn.name}: ${object.seat_assignment}`}
              value={addOn.price_to_currency}
            />
          ))
        ) : (
          <Row
            key={`addon-${index}`}
            label={`${addOn.quantity} x ${addOn.name}`}
            value={addOn.price_to_currency}
          />
        )
      ))}

      {membershipPrice && (
        <Row
          label={`1 x ${selectedMembershipProduct.name}`}
          value={membershipPrice.price_to_currency}
        />
      )}

      {showPromoLine && (
        <>
          <Divider />
          <Row
            label={promoCode.discount_label}
            value={`-${formatter.format(ticketReservation.discount)}`}
          />
        </>
      )}

      <Divider />

      {/* Subtotal */}
      <Row label="Subtotal" value={subtotalDisplay} />

      {/* Abstract order items (e.g. donations) */}
      {(ticketReservation.abstract_order_items || []).map((item, index) =>
        item.custom_charge_type_type === 'CustomChargeTypes::Donation' ? (
          <Row key={`aoi-${index}`} label={item.name} value={item.summed_price_to_currency} />
        ) : (
          <Row
            key={`aoi-${index}`}
            label={`${item.quantity} x ${item.price_to_currency} ${item.name}`}
            value={item.summed_price_to_currency}
          />
        )
      )}

      {/* Fees */}
      {ticketFees.map((fee, index) => (
        <Row key={`fee-${index}`} label={fee.name} value={formatter.format(fee.value)} />
      ))}

      {membershipFees > 0 && (
        <Row label="Membership Fees" value={formatter.format(membershipFees)} />
      )}

      {(parseFloat(ticketReservation.taxes || 0) + membershipTax) > 0 && (
        <Row
          label="Taxes"
          value={formatter.format(parseFloat(ticketReservation.taxes || 0) + membershipTax)}
        />
      )}

      {ticketReservation.gift_up_redemption && (
        <>
          <Divider />
          <Row
            label={`${ticketReservation.gift_up_redemption.gift_up_giftcard.title} (${ticketReservation.gift_up_redemption.gift_up_giftcard.code})`}
            value={`-${formatter.format(ticketReservation.gift_up_redemption.redeemed_amount)}`}
          />
        </>
      )}

      {(ticketReservation.applied_gift_card_summaries || []).length > 0 && (
        <>
          <Divider />
          {ticketReservation.applied_gift_card_summaries.map((giftCard) => (
            <Row
              key={`gift-card-${giftCard.id}`}
              label={`Gift card •••• ${giftCard.code_last4}`}
              value={`-${formatter.format(giftCard.amount_cents / 100)}`}
            />
          ))}
        </>
      )}

      <Divider />

      <Row label="Total" value={totalDisplay} bold />

      {ticketInsurance && ticketInsurance.quote_id && (
        <Row
          label="Insurance"
          value={formatter.format(ticketInsurance.premium_amount)}
          bold
        />
      )}

      {selectedMembershipProduct && (
        <div className="cwb-public-summary-note">
          Your membership will be processed as a separate charge and will appear separately on your bank statement.
        </div>
      )}

      {ticketReservation.donation_separated && (
        <div className="cwb-public-summary-note">
          Your donation will be processed as a separate charge and will appear separately on your bank statement.
        </div>
      )}

      {confirm.checkout_widget_memo && confirm.checkout_widget_memo.length > 0 && (
        <div className="cwb-public-summary-memo">{confirm.checkout_widget_memo}</div>
      )}
    </div>
  );
};

export default CustomTotalsTable;
